import React, {useCallback, useState} from 'react';
import {Box, Typography} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import {useTranslation} from 'react-i18next';
import ArrModal from '../../common/arr-modal/ArrModal';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import './ExportTrueLawModal.scss';
import {ReactComponent as TrueLawModalIcon} from '../../../assets/images/export-truelaw-modal.svg';
import {useSaveExportConfirmation} from '../../../hooks/saved-search/useExportTrueLawAI';
import {ToastType} from '../../../shared/constants';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ISavedSearchResponse} from '../../../api/types/savedsearch.types';

interface ExportTrueLawModalProps {
  open: boolean;
  selectedSavedSearch: ISavedSearchResponse | null;
  onClose: () => void;
}

const ExportTrueLawModal: React.FC<ExportTrueLawModalProps> = ({
  open,
  selectedSavedSearch,
  onClose,
}) => {
  const {t} = useTranslation();
  const [exportSetName, setExportSetName] = useState<string>('');
  const [disclaimerInput, setDisclaimerInput] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const [touched, setTouched] = useState({
    exportName: false,
    disclaimerInput: false,
  });
  const MAX_NAME_LENGTH = 50;
  const NAME_REGEX = /^[A-Za-z0-9 _-]+$/;
  const trimmedExportName = exportSetName.trim();
  const isExportNameSpecialCharValid =
    trimmedExportName.length === 0 || NAME_REGEX.test(trimmedExportName);
  const isExportNameValid =
    trimmedExportName.length > 0 &&
    trimmedExportName.length <= MAX_NAME_LENGTH &&
    isExportNameSpecialCharValid;
  const isIUnderstandValid =
    disclaimerInput.trim().toLowerCase() ===
    t('exportTrueLawAI.exportConfirmation.disclaimerinputtext');
  const {mutate: mutateSave} = useSaveExportConfirmation();
  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const handleSubmit = () => {
    if (isExportNameValid && isIUnderstandValid) {
      mutateSave(
        {
          exportSetName: trimmedExportName,
          contextTypeCode: 'SAVEDSEARCH',
          contextIds: String(selectedSavedSearch?.searchID),
        },
        {
          onSuccess: () => {
            triggerToast(
              t('exportTrueLawAI.exportConfirmation.submit'),
              ToastType.Success
            );
            handleCloseModal();
          },
          onError: (error: any) => {
            const detail: string | undefined = error?.response?.data?.detail;
            const rpmxMatch = detail?.match(/\(RPMX ERROR\s+(\d+)\)/i);
            const rpmxCode = rpmxMatch ? rpmxMatch[1] : null;
            if (rpmxCode === '92001000001') {
              triggerToast(
                t('exportTrueLawAI.exportConfirmation.duplicateExportSetError'),
                ToastType.Error
              );
            } else {
              triggerToast(
                t('exportTrueLawAI.exportConfirmation.error'),
                ToastType.Error
              );
            }
          },
        }
      );
    }
  };

  const handleClear = () => {
    setExportSetName('');
    setDisclaimerInput('');
    setTouched({exportName: false, disclaimerInput: false});
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    if (name === 'exportSetName') setExportSetName(value);
    if (name === 'disclaimerinput') setDisclaimerInput(value);
  };

  const handleBlur = (fieldName: keyof typeof touched) => {
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  const handleCloseModal = useCallback(() => {
    handleClear();
    onClose();
  }, []);

  return (
    <>
      <ArrModal
        showModal={open}
        showClose={false}
        className="export-truelaw"
        onClose={onClose}
        dataTestId="export-truelaw"
        buttons={[
          {
            id: '1',
            label: t('common.cancel'),
            onClick: handleCloseModal,
            variant: 'secondary',
            dataTestId: 'export-truelaw-cancel-button',
          },
          {
            id: '2',
            label: t('common.submit'),
            onClick: handleSubmit,
            variant: 'primary',
            disabled: !isExportNameValid || !isIUnderstandValid,
            dataTestId: 'export-truelaw-submit-button',
          },
        ]}
      >
        <TrueLawModalIcon
          data-testid="truelawmodal-icon"
          className="export-truelaw-icon"
        />

        <Box className="export-confirmation-box">
          <Typography
            className="typo-text headline3_size title"
            data-testid={`export-true-law-modal-title`}
          >
            {t('exportTrueLawAI.exportConfirmation.title')}
          </Typography>

          <ArrTooltipWrapper
            trigger={<InfoIcon fontSize="medium" className="info-icon" />}
          >
            {t('exportTrueLawAI.exportConfirmation.tooltipText')}
          </ArrTooltipWrapper>
        </Box>

        <Typography
          className="export-confirmation__subtitle"
          data-testid={`export-true-law-modal-description`}
        >
          {t('exportTrueLawAI.exportConfirmation.description', {
            count: Number(selectedSavedSearch?.result),
          })}
        </Typography>

        <Typography
          className="export-confirmation__subtitle export-confirmation-text"
          data-testid={`export-true-law-modal-text-document-confirmation-text`}
        >
          {t('exportTrueLawAI.exportConfirmation.textDocumentConfirmationText')}
        </Typography>

        <Typography
          className="export-confirmation__label"
          data-testid={`export-true-law-modal-export-set`}
        >
          {t('exportTrueLawAI.exportConfirmation.exportSet')}
        </Typography>

        <Box className="exportset-input-box">
          <ArrTextField
            id="exportset-input"
            label={t('exportTrueLawAI.exportConfirmation.exportSetInputLabel')}
            name="exportSetName"
            value={exportSetName}
            onChange={handleChange}
            onBlur={() => handleBlur('exportName')}
            showErrorIcon={false}
            dataTestId="export-modal-exportset-input"
            required
            state={
              trimmedExportName.length > MAX_NAME_LENGTH ||
              !isExportNameSpecialCharValid
                ? 'error'
                : 'enabled'
            }
            supportText={
              trimmedExportName.length > MAX_NAME_LENGTH
                ? t('exportTrueLawAI.exportConfirmation.maxLengthError')
                : !isExportNameSpecialCharValid
                  ? t(
                      'exportTrueLawAI.exportConfirmation.specialCharacterError'
                    )
                  : ''
            }
          />
        </Box>

        <Box className="disclaimerText-box">
          <Typography
            variant="body1"
            className="disclaimerText1"
            data-testid={`export-true-law-modal-disclaimer-text1`}
          >
            {t('exportTrueLawAI.exportConfirmation.disclaimerText1')}
          </Typography>

          <Typography
            variant="body2"
            className="disclaimerText2"
            data-testid={`export-true-law-modal-disclaimer-text2`}
          >
            {t('exportTrueLawAI.exportConfirmation.disclaimerText2')}
          </Typography>

          <ArrTextField
            id="disclaimerText-input"
            name="disclaimerinput"
            label={t('exportTrueLawAI.exportConfirmation.disclaimerinputlabel')}
            value={disclaimerInput}
            onChange={handleChange}
            dataTestId="export-modal-disclaimer-text"
            onBlur={() => handleBlur('disclaimerInput')}
            state={
              touched.disclaimerInput &&
              disclaimerInput.trim().toLowerCase() !==
                t('exportTrueLawAI.exportConfirmation.disclaimerinputtext')
                ? 'error'
                : 'enabled'
            }
            supportText={
              touched.disclaimerInput &&
              disclaimerInput.trim().toLowerCase() !==
                t('exportTrueLawAI.exportConfirmation.disclaimerinputtext')
                ? t('exportTrueLawAI.exportConfirmation.exportAcknowledge')
                : ''
            }
          />
        </Box>
      </ArrModal>
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
    </>
  );
};

export default ExportTrueLawModal;
